import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { PdfAnnotation } from '../models';

export type FmedaPdfWindowSnapshot = {
    pdfUrl: string;
    annotations: PdfAnnotation[];
    focusedDesignator: string;
    updatedAt: number;
};

type SnapshotMessage = {
    type: 'snapshot';
    sourceId: string;
    snapshot: FmedaPdfWindowSnapshot;
};

type FocusMessage = {
    type: 'focus';
    sourceId: string;
    designator: string;
    updatedAt: number;
};

type PopoutState = {
    isOpen: boolean;
    updatedAt: number;
};

type PopoutStateMessage = {
    type: 'popout-state';
    sourceId: string;
    state: PopoutState;
};

type FmedaPdfWindowMessage = SnapshotMessage | FocusMessage | PopoutStateMessage;

const EMPTY_SNAPSHOT: FmedaPdfWindowSnapshot = {
    pdfUrl: '',
    annotations: [],
    focusedDesignator: '',
    updatedAt: 0
};

@Injectable({ providedIn: 'root' })
export class FmedaPdfWindowSyncService {
    private readonly popoutLeaseMs = 7000;
    private readonly channelName = 'fmeda-pdf-window-sync';
    private readonly storageKey = 'fmeda-pdf-window-sync-snapshot';
    private readonly popoutStateStorageKey = 'fmeda-pdf-window-sync-popout-state';
    private readonly sourceId = `fmeda-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    private readonly channel = typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(this.channelName)
        : null;

    readonly snapshot$ = new BehaviorSubject<FmedaPdfWindowSnapshot>(this.readPersistedSnapshot());
    readonly popoutOpen$ = new BehaviorSubject<boolean>(this.isPopoutActive(this.readPersistedPopoutState()));
    readonly focusFromRemote$ = new Subject<string>();

    constructor() {
        this.channel?.addEventListener('message', this.handleChannelMessage);
        window.addEventListener('storage', this.handleStorageEvent);
    }

    getSnapshot(): FmedaPdfWindowSnapshot {
        return this.snapshot$.value;
    }

    publishSnapshot(snapshot: Omit<FmedaPdfWindowSnapshot, 'updatedAt'>): void {
        const nextSnapshot: FmedaPdfWindowSnapshot = {
            pdfUrl: snapshot.pdfUrl,
            annotations: snapshot.annotations,
            focusedDesignator: snapshot.focusedDesignator.trim(),
            updatedAt: Date.now()
        };

        this.applySnapshot(nextSnapshot, false);
        this.postMessage({
            type: 'snapshot',
            sourceId: this.sourceId,
            snapshot: nextSnapshot
        });
    }

    publishFocus(designator: string | null): void {
        const normalized = (designator ?? '').trim();
        const current = this.snapshot$.value;
        const nextSnapshot: FmedaPdfWindowSnapshot = {
            ...current,
            focusedDesignator: normalized,
            updatedAt: Date.now()
        };

        this.applySnapshot(nextSnapshot, false);
        this.postMessage({
            type: 'focus',
            sourceId: this.sourceId,
            designator: normalized,
            updatedAt: nextSnapshot.updatedAt
        });
    }

    setPopoutOpen(isOpen: boolean): void {
        const nextState: PopoutState = {
            isOpen,
            updatedAt: Date.now()
        };

        this.applyPopoutState(nextState);
        this.postMessage({
            type: 'popout-state',
            sourceId: this.sourceId,
            state: nextState
        });
    }

    private readonly handleChannelMessage = (event: MessageEvent<FmedaPdfWindowMessage>): void => {
        const message = event.data;
        if (!message || message.sourceId === this.sourceId) {
            return;
        }

        if (message.type === 'snapshot') {
            this.applySnapshot(message.snapshot, true);
            return;
        }

        if (message.type === 'popout-state') {
            this.applyPopoutState(message.state);
            return;
        }

        const current = this.snapshot$.value;
        const nextSnapshot: FmedaPdfWindowSnapshot = {
            ...current,
            focusedDesignator: message.designator,
            updatedAt: message.updatedAt
        };

        this.applySnapshot(nextSnapshot, true);
        this.focusFromRemote$.next(message.designator);
    };

    private readonly handleStorageEvent = (event: StorageEvent): void => {
        if (event.key === this.popoutStateStorageKey && event.newValue) {
            const state = this.parsePopoutState(event.newValue);
            if (state) {
                this.applyPopoutState(state);
            }
            return;
        }

        if (event.key !== this.storageKey || !event.newValue) {
            return;
        }

        const snapshot = this.parseSnapshot(event.newValue);
        if (snapshot) {
            this.applySnapshot(snapshot, true);
        }
    };

    private applySnapshot(snapshot: FmedaPdfWindowSnapshot, fromRemote: boolean): void {
        if (snapshot.updatedAt < this.snapshot$.value.updatedAt) {
            return;
        }

        this.snapshot$.next(snapshot);
        this.persistSnapshot(snapshot);

        if (fromRemote) {
            this.focusFromRemote$.next(snapshot.focusedDesignator);
        }
    }

    private postMessage(message: FmedaPdfWindowMessage): void {
        this.channel?.postMessage(message);
    }

    private applyPopoutState(state: PopoutState): void {
        const current = this.readPersistedPopoutState();
        if (state.updatedAt < current.updatedAt) {
            return;
        }

        this.popoutOpen$.next(this.isPopoutActive(state));
        localStorage.setItem(this.popoutStateStorageKey, JSON.stringify(state));
    }

    private persistSnapshot(snapshot: FmedaPdfWindowSnapshot): void {
        localStorage.setItem(this.storageKey, JSON.stringify(snapshot));
    }

    private readPersistedSnapshot(): FmedaPdfWindowSnapshot {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) {
            return EMPTY_SNAPSHOT;
        }

        return this.parseSnapshot(raw) ?? EMPTY_SNAPSHOT;
    }

    private parseSnapshot(raw: string): FmedaPdfWindowSnapshot | null {
        try {
            const parsed = JSON.parse(raw) as Partial<FmedaPdfWindowSnapshot>;
            return {
                pdfUrl: typeof parsed.pdfUrl === 'string' ? parsed.pdfUrl : '',
                annotations: Array.isArray(parsed.annotations) ? parsed.annotations as PdfAnnotation[] : [],
                focusedDesignator: typeof parsed.focusedDesignator === 'string' ? parsed.focusedDesignator : '',
                updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
            };
        } catch {
            return null;
        }
    }

    private readPersistedPopoutState(): PopoutState {
        const raw = localStorage.getItem(this.popoutStateStorageKey);
        if (!raw) {
            return { isOpen: false, updatedAt: 0 };
        }

        const parsed = this.parsePopoutState(raw) ?? { isOpen: false, updatedAt: 0 };
        return this.isPopoutActive(parsed) ? parsed : { isOpen: false, updatedAt: 0 };
    }

    private isPopoutActive(state: PopoutState | null | undefined): boolean {
        if (!state?.isOpen || !state.updatedAt) {
            return false;
        }

        return Date.now() - state.updatedAt <= this.popoutLeaseMs;
    }

    private parsePopoutState(raw: string): PopoutState | null {
        try {
            const parsed = JSON.parse(raw) as Partial<PopoutState>;
            return {
                isOpen: parsed.isOpen === true,
                updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
            };
        } catch {
            return null;
        }
    }
}